import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import FareCard from "./FareCard";
import styles from "../../styles";
import type { BrandFeatureInfo } from "./BrandFeaturesModal";

export interface FareCardData {
  label: string;
  price: number;
  currency: string;
  baggage: string;
  cancellation: string;
  addOns: string;
  cabinCode?: string;
  brandFeatures?: BrandFeatureInfo[];
}

interface FlippableFareCardProps {
  fare: FareCardData;
  brandFeatures: BrandFeatureInfo[];
  brandName: string;
  selected: boolean;
  onClick: () => void;
}

export default function FlippableFareCard({
  fare,
  brandFeatures,
  brandName,
  selected,
  onClick,
}: FlippableFareCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const hasBrandFeatures =
    Array.isArray(brandFeatures) && brandFeatures.length > 0;

  useEffect(() => {
    if (!isFlipped) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setIsFlipped(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFlipped]);

  const handleInfoClick = () => {
    if (hasBrandFeatures) setIsFlipped(true);
  };

  return (
    <div
      ref={cardRef}
      className={`relative w-full min-h-[175px] sm:min-h-[200px] ${styles.flexCol}`}
      onClick={(e) => {
        if (!(e.target as HTMLElement).closest("[data-info-icon]")) {
          onClick();
        }
      }}
    >
      <motion.div
        className="relative w-full min-h-[175px] sm:min-h-[200px] cursor-pointer"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{
          transformStyle: "preserve-3d",
          perspective: "1000px",
        }}
      >
        {/* Front - Fare card */}
        <motion.div
          className="absolute inset-0 w-full h-full"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <div className={`w-full h-full ${styles.flexCol}`}>
            <FareCard
              {...fare}
              selected={selected}
              onInfoClick={hasBrandFeatures ? handleInfoClick : undefined}
            />
          </div>
        </motion.div>

        {/* Back - Brand features */}
        <motion.div
          className="absolute inset-0 w-full h-full"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <div
            className={`w-full h-full p-3 sm:p-4 rounded-xl shadow-md bg-white text-[#0B2F9F] ${styles.flexCol} overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 pb-2 mb-2 flex-shrink-0">
              <h3 className="text-sm font-bold text-primary">
                {brandName} – Features
              </h3>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0">
              {hasBrandFeatures ? (
                <ul className="space-y-2 pr-1">
                  {brandFeatures.map((f) => (
                    <li
                      key={f.id}
                      className="text-xs sm:text-sm text-gray-700 flex items-start gap-2"
                    >
                      <span className="text-primary mt-0.5 flex-shrink-0">
                        •
                      </span>
                      <span>{f.commercialName}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500">No features available</p>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
